import { useInfiniteQuery } from '@tanstack/react-query'
import { Select, SelectProps, Typography } from 'antd'
import { useState } from 'react'

import {
  useClassifierServiceGetIcd10AmClassifierElementEndpoint,
  useClassifierServiceGetCommonClassifierElementListEndpointKey
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierService,
  ClassifierType,
  Icd10AmElementType
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useClassifierId, useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  /** Only elements of this special list, e.g. the opportunistic diseases. */
  type?: Icd10AmElementType
  /** Ids that cannot be chosen, e.g. diseases already listed. */
  excludeIds?: string[]
  /** False shows only the name, when the code is a field of its own (20 / 20.1). */
  withCode?: boolean
} & SelectProps<string>

/** A single ICD-10-AM element (20 / 20.1, "Alte" of 56); only the id is stored. */
export const HivIcd10Select = ({
  type,
  excludeIds,
  withCode = true,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const [search, setSearch] = useState<string>()
  const debouncedSearch = useDebounce(search)

  const classifierId = useClassifierId(
    ClassifierType.IC_D10AM,
    ClassifierCategory.DIAGNOSTIC
  )

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useClassifierServiceGetCommonClassifierElementListEndpointKey,
      String(classifierId),
      debouncedSearch,
      type
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await ClassifierService.getIcd10AmClassifierElementListEndpoint(
          String(classifierId),
          pageParam,
          DEFAULT_PER_PAGE,
          undefined,
          debouncedSearch,
          type
        )

      return {
        data: response.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!classifierId
  })

  const { data: selected } =
    useClassifierServiceGetIcd10AmClassifierElementEndpoint(
      { id: String(classifierId), elementId: String(props.value) },
      [classifierId, props.value],
      { enabled: !!classifierId && !!props.value, retry: false }
    )

  const excluded = new Set(excludeIds ?? [])
  const items = [
    ...(selected?.id ? [selected] : []),
    ...(data?.pages.flatMap((page) => page.data) ?? [])
  ].filter(
    (item, index, all) =>
      !!item?.id &&
      (!excluded.has(item.id) || item.id === props.value) &&
      all.findIndex((x) => x?.id === item.id) === index
  )

  return (
    <Select
      allowClear
      showSearch
      filterOption={false}
      onSearch={setSearch}
      loading={isFetching}
      placeholder={t('common.select_from_list')}
      options={items.map((item) => ({
        value: item?.id,
        label: withCode
          ? `${item?.code} - ${item?.translation?.[currentLang.value]}`
          : item?.translation?.[currentLang.value]
      }))}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (
          Math.ceil(target.scrollTop + target.offsetHeight) ===
          target.scrollHeight
        )
          fetchNextPage()
      }}
      {...props}
    />
  )
}

/** The code of an ICD-10-AM element, e.g. of the diagnosis chosen in 20. */
export const useIcd10Code = (elementId?: string | null) => {
  const classifierId = useClassifierId(
    ClassifierType.IC_D10AM,
    ClassifierCategory.DIAGNOSTIC
  )

  const { data } = useClassifierServiceGetIcd10AmClassifierElementEndpoint(
    { id: String(classifierId), elementId: String(elementId) },
    [classifierId, elementId],
    { enabled: !!classifierId && !!elementId, retry: false }
  )

  return elementId ? data?.code : undefined
}

/** The code of the selected ICD-10-AM element, shown as its own field (20.1). */
export const Icd10CodeText = ({ elementId }: { elementId?: string }) => {
  const code = useIcd10Code(elementId)

  return <Typography.Text>{code || '—'}</Typography.Text>
}
